/**
 * Desktop Flows (RPA) tools — list/run/monitor/cancel desktop flows, discover
 * the connections that runs require, and inspect the machines and machine
 * groups the runs land on. Backed by the documented Dataverse Web API surface
 * (see src/api/desktop-flows-api.ts).
 */

import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  describeSessionStatus,
  describeMachineStatus,
  KEY_DELIVERY_STATUS,
  MACHINE_GROUP_TYPE,
  MACHINE_HOSTING_TYPE,
  MACHINE_STATE,
  TERMINAL_SESSION_STATUSES,
  type DesktopFlowsApi,
  type FlowMachine,
  type FlowSessionSummary,
} from "../api/desktop-flows-api.js";
import { GUID_PATTERN } from "../utils/security.js";
import { logger } from "../utils/logger.js";

const FLOW_ID_DESCRIPTION = "The desktop flow's workflow ID (GUID) from list_desktop_flows";
const SESSION_ID_DESCRIPTION = "The run's flow session ID (GUID) returned by run_desktop_flow";

// =============================================================================
// Tool definitions
// =============================================================================

export const listDesktopFlowsTool: Tool = {
  name: "list_desktop_flows",
  description:
    "List desktop flows (RPA/UI automation built with Power Automate for desktop) in the environment, with the workflow IDs used to run them.",
  inputSchema: {
    type: "object",
    properties: {
      includeDrafts: {
        type: "boolean",
        description: "Also include unpublished (draft) desktop flows",
      },
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum flows to return" },
    },
  },
};

export const getDesktopFlowTool: Tool = {
  name: "get_desktop_flow",
  description:
    "Get a desktop flow's details plus its declared input and output variables (schema) — check this before run_desktop_flow to know what inputs to pass.",
  inputSchema: {
    type: "object",
    properties: {
      flowId: { type: "string", description: FLOW_ID_DESCRIPTION },
    },
    required: ["flowId"],
  },
};

export const runDesktopFlowTool: Tool = {
  name: "run_desktop_flow",
  description:
    "Trigger a desktop flow (RPA) run on a registered machine via a desktop-flows connection. Attended runs need a signed-in user at the machine; unattended runs need a Process license on the machine. Returns a session ID to poll with get_desktop_flow_run. Limit: 70 runs/minute per connection.",
  inputSchema: {
    type: "object",
    properties: {
      flowId: { type: "string", description: FLOW_ID_DESCRIPTION },
      connectionName: {
        type: "string",
        description:
          "Desktop-flows connection name (from the connection's URL in make.powerautomate.com), or a connection reference logical name when isConnectionReference is true. Discover references with list_desktop_flow_connections.",
      },
      isConnectionReference: {
        type: "boolean",
        description: "Treat connectionName as a solution connection reference logical name",
      },
      runMode: {
        type: "string",
        enum: ["attended", "unattended"],
        description: "attended = a user is signed in at the machine; unattended = background run",
      },
      inputs: {
        type: "object",
        description:
          "Input variables for the flow, as key/value pairs matching get_desktop_flow's input schema",
      },
      timeoutSeconds: {
        type: "number",
        minimum: 1,
        maximum: 86400,
        description: "Run timeout in seconds (default 3 hours, max 24 hours)",
      },
      priority: { type: "string", enum: ["normal", "high"], description: "Queue priority" },
    },
    required: ["flowId", "connectionName", "runMode"],
  },
};

export const getDesktopFlowRunsTool: Tool = {
  name: "get_desktop_flow_runs",
  description:
    "List recent desktop flow runs (newest first) — across all desktop flows or scoped to one — with status, timing, and error summaries.",
  inputSchema: {
    type: "object",
    properties: {
      flowId: {
        type: "string",
        description: `${FLOW_ID_DESCRIPTION}. Omit to list recent runs across all desktop flows`,
      },
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum runs to return" },
    },
  },
};

export const getDesktopFlowRunTool: Tool = {
  name: "get_desktop_flow_run",
  description:
    "Get one desktop flow run's status. Returns outputs when the run succeeded, and full error details (code, message, machine) when it failed.",
  inputSchema: {
    type: "object",
    properties: {
      sessionId: { type: "string", description: SESSION_ID_DESCRIPTION },
    },
    required: ["sessionId"],
  },
};

export const cancelDesktopFlowRunTool: Tool = {
  name: "cancel_desktop_flow_run",
  description:
    "Cancel a queued or running desktop flow run. Needs owner-level access to the run — runs you triggered qualify.",
  inputSchema: {
    type: "object",
    properties: {
      sessionId: { type: "string", description: SESSION_ID_DESCRIPTION },
    },
    required: ["sessionId"],
  },
};

export const getDesktopFlowRunLogsTool: Tool = {
  name: "get_desktop_flow_run_logs",
  description:
    "Get a desktop flow run's action-level logs. Tries the V2 log store (flowlog) first, then the V1 run context; says plainly when the run has no action-level logs (common for API-triggered runs — V2 logging is documented for connector-triggered runs).",
  inputSchema: {
    type: "object",
    properties: {
      sessionId: { type: "string", description: SESSION_ID_DESCRIPTION },
      tail: {
        type: "number",
        minimum: 1,
        maximum: 200,
        description: "Only the last N log entries (default 50)",
      },
    },
    required: ["sessionId"],
  },
};

export const diagnoseDesktopFlowRunTool: Tool = {
  name: "diagnose_desktop_flow_run",
  description:
    "Diagnose a desktop flow run: decodes the failure, checks the machine it ran on (status, heartbeat), translates licensing errors into the exact license to request, and pulls the log tail when available.",
  inputSchema: {
    type: "object",
    properties: {
      sessionId: { type: "string", description: SESSION_ID_DESCRIPTION },
    },
    required: ["sessionId"],
  },
};

export const listDesktopFlowConnectionsTool: Tool = {
  name: "list_desktop_flow_connections",
  description:
    "Find desktop-flows connection references usable as run_desktop_flow's connectionName (with isConnectionReference: true). Explains how to create/locate a connection when none exist.",
  inputSchema: {
    type: "object",
    properties: {},
  },
};

export const listMachinesTool: Tool = {
  name: "list_machines",
  description:
    "List registered desktop-flow (RPA) machines with live status, last heartbeat, capacity, hosting type, and group membership.",
  inputSchema: {
    type: "object",
    properties: {
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum machines to return" },
    },
  },
};

export const getMachineTool: Tool = {
  name: "get_machine",
  description:
    "Get one desktop-flow machine's full detail: status, heartbeat, session capacity, agent version, hosted-machine errors, and group-key health.",
  inputSchema: {
    type: "object",
    properties: {
      machineId: { type: "string", description: "The machine's flowmachine ID (GUID) from list_machines" },
    },
    required: ["machineId"],
  },
};

export const listMachineGroupsTool: Tool = {
  name: "list_machine_groups",
  description:
    "List desktop-flow machine groups (load balancing / high availability for unattended RPA runs) with type, member count, and provisioning state.",
  inputSchema: {
    type: "object",
    properties: {
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum groups to return" },
    },
  },
};

export const restartHostedMachineTool: Tool = {
  name: "restart_hosted_machine",
  description:
    "Restart a Microsoft-hosted RPA machine (interrupts any in-flight run on it). Only works on hosted machines — physical machines must be restarted at the OS.",
  inputSchema: {
    type: "object",
    properties: {
      machineId: { type: "string", description: "The machine's flowmachine ID (GUID) from list_machines" },
    },
    required: ["machineId"],
  },
};

// =============================================================================
// Input schemas
// =============================================================================

const listDesktopFlowsSchema = z.object({
  includeDrafts: z.boolean().optional(),
  top: z.number().int().min(1).max(250).optional(),
});

const getDesktopFlowSchema = z.object({
  flowId: z.string().regex(GUID_PATTERN, "flowId must be a GUID"),
});

const runDesktopFlowSchema = z.object({
  flowId: z.string().regex(GUID_PATTERN, "flowId must be a GUID"),
  connectionName: z.string().min(1),
  isConnectionReference: z.boolean().optional(),
  runMode: z.enum(["attended", "unattended"]),
  inputs: z.record(z.string(), z.unknown()).optional(),
  timeoutSeconds: z.number().int().min(1).max(86400).optional(),
  priority: z.enum(["normal", "high"]).optional(),
});

const getDesktopFlowRunsSchema = z.object({
  flowId: z.string().regex(GUID_PATTERN, "flowId must be a GUID").optional(),
  top: z.number().int().min(1).max(250).optional(),
});

const sessionIdSchema = z.object({
  sessionId: z.string().regex(GUID_PATTERN, "sessionId must be a GUID"),
});

const runLogsSchema = z.object({
  sessionId: z.string().regex(GUID_PATTERN, "sessionId must be a GUID"),
  tail: z.number().int().min(1).max(200).optional(),
});

const listMachinesSchema = z.object({
  top: z.number().int().min(1).max(250).optional(),
});

const getMachineSchema = z.object({
  machineId: z.string().regex(GUID_PATTERN, "machineId must be a GUID"),
});

const listMachineGroupsSchema = z.object({
  top: z.number().int().min(1).max(250).optional(),
});

// =============================================================================
// Formatting helpers
// =============================================================================

function formatDuration(startedon?: string, completedon?: string): string | undefined {
  if (!startedon) return undefined;
  const start = Date.parse(startedon);
  const end = completedon ? Date.parse(completedon) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return undefined;
  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function formatRunLine(run: FlowSessionSummary): string {
  const status = describeSessionStatus(run.statuscode);
  const started = run.startedon ?? "not started";
  const duration = formatDuration(run.startedon, run.completedon);
  const machine = run.gateway ? ` on ${run.gateway}` : "";
  const error = run.errorcode ? ` — ${run.errorcode}: ${run.errormessage ?? ""}`.trimEnd() : "";
  return `  ${run.flowsessionid} — ${status}${machine} — started ${started}${duration ? ` (${duration})` : ""}${error}`;
}

const NO_CONNECTIONS_GUIDANCE = [
  "No desktop-flows connection references found in this environment.",
  "",
  "run_desktop_flow needs a desktop-flows connection (it carries the target machine and its credentials). Connections are per-user and created once:",
  "  1. Go to make.powerautomate.com > Connections > New connection > Desktop flows.",
  "  2. Pick the machine (or machine group) and sign-in method.",
  '  3. Open the new connection; its URL contains "connectionName=<value>" — pass that value as connectionName.',
  "Alternatively, add the connection to a solution as a connection reference; it will then appear here and can be used with isConnectionReference: true.",
].join("\n");

// =============================================================================
// Handlers
// =============================================================================

export async function handleListDesktopFlows(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = listDesktopFlowsSchema.parse(input);
  logger.info(parsed, "Executing list_desktop_flows");
  const flows = await api.listDesktopFlows(parsed);
  if (flows.length === 0) {
    return [
      "No desktop flows found in this environment.",
      "Desktop flows are authored in Power Automate for desktop (PAD) on a Windows machine and appear here once saved to this environment.",
    ].join("\n");
  }
  return [
    `Desktop flows (${flows.length}):`,
    ...flows.map(
      (f) =>
        `  ${f.name} (${f.workflowid})${f.statecode === 0 ? " [draft]" : ""}${f.description ? ` — ${f.description}` : ""}`,
    ),
    "",
    "Run one with run_desktop_flow (check its inputs first with get_desktop_flow).",
  ].join("\n");
}

export async function handleGetDesktopFlow(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = getDesktopFlowSchema.parse(input);
  logger.info({ flowId: parsed.flowId }, "Executing get_desktop_flow");
  const detail = await api.getDesktopFlow(parsed.flowId);
  const lines = [
    `Desktop flow: ${detail.flow.name} (${detail.flow.workflowid})`,
    detail.flow.description ? `Description: ${detail.flow.description}` : undefined,
    `Modified: ${detail.flow.modifiedon ?? "unknown"}`,
    "",
    detail.inputSchema
      ? `Input variables (pass matching keys in run_desktop_flow's inputs):\n${JSON.stringify(detail.inputSchema, null, 2)}`
      : "Input variables: none declared — run without inputs.",
    detail.outputSchema
      ? `Output variables:\n${JSON.stringify(detail.outputSchema, null, 2)}`
      : "Output variables: none declared.",
  ].filter((l): l is string => l !== undefined);
  return lines.join("\n");
}

export async function handleRunDesktopFlow(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = runDesktopFlowSchema.parse(input);
  logger.info(
    { flowId: parsed.flowId, runMode: parsed.runMode },
    "Executing run_desktop_flow",
  );
  const result = await api.runDesktopFlow(parsed);
  const lines = [
    `Desktop flow run queued (${parsed.runMode}).`,
    `Session ID: ${result.flowsessionId}`,
    "Poll status with get_desktop_flow_run; cancel with cancel_desktop_flow_run.",
  ];
  if (result.licenseWarning) {
    lines.push("", `License warning: ${result.licenseWarning}`);
  }
  return lines.join("\n");
}

export async function handleGetDesktopFlowRuns(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = getDesktopFlowRunsSchema.parse(input);
  logger.info(parsed, "Executing get_desktop_flow_runs");
  const runs = await api.listRuns(parsed);
  if (runs.length === 0) {
    return parsed.flowId
      ? "No runs found for this desktop flow (you can only see runs you own or that are shared with you)."
      : "No desktop flow runs found in this environment (you can only see runs you own or that are shared with you).";
  }
  return [`Desktop flow runs (${runs.length}, newest first):`, ...runs.map(formatRunLine)].join(
    "\n",
  );
}

export async function handleGetDesktopFlowRun(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = sessionIdSchema.parse(input);
  logger.info({ sessionId: parsed.sessionId }, "Executing get_desktop_flow_run");
  const detail = await api.getRun(parsed.sessionId);
  const s = detail.session;
  const status = describeSessionStatus(s.statuscode);
  const terminal = s.statuscode !== undefined && TERMINAL_SESSION_STATUSES.has(s.statuscode);

  const lines = [
    `Run ${s.flowsessionid}: ${status}${terminal ? "" : " (still in progress — poll again)"}`,
    s.gateway ? `Machine: ${s.gateway}` : undefined,
    `Started: ${s.startedon ?? "not started"}${s.completedon ? ` — Completed: ${s.completedon}` : ""}`,
    formatDuration(s.startedon, s.completedon)
      ? `Duration: ${formatDuration(s.startedon, s.completedon)}`
      : undefined,
  ].filter((l): l is string => l !== undefined);

  if (s.statuscode === 4) {
    lines.push(
      detail.outputs !== undefined
        ? `Outputs:\n${JSON.stringify(detail.outputs, null, 2)}`
        : "Outputs: none (the flow declares no output variables).",
    );
  } else if (s.errorcode || s.errormessage) {
    lines.push(
      `Error code: ${s.errorcode ?? "unknown"}`,
      `Error message: ${s.errormessage ?? "none"}`,
    );
    if (s.errordetails) lines.push(`Error details: ${s.errordetails}`);
  }

  return lines.join("\n");
}

export async function handleCancelDesktopFlowRun(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = sessionIdSchema.parse(input);
  logger.info({ sessionId: parsed.sessionId }, "Executing cancel_desktop_flow_run");
  await api.cancelRun(parsed.sessionId);
  return `Cancellation requested for run ${parsed.sessionId}. Confirm the final state with get_desktop_flow_run (it should reach Cancelled).`;
}

export async function handleGetDesktopFlowRunLogs(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = runLogsSchema.parse(input);
  logger.info({ sessionId: parsed.sessionId }, "Executing get_desktop_flow_run_logs");
  const result = await api.getRunLogs(parsed.sessionId);

  if (result.source === "flowlog" && result.entries) {
    const tail = parsed.tail ?? 50;
    const shown = result.entries.slice(-tail);
    return [
      `Run logs (V2 flowlog store): ${result.entries.length} entries${shown.length < result.entries.length ? `, showing last ${shown.length}` : ""}:`,
      ...shown.map((e) => `  ${e.createdon ?? ""} ${e.data ?? JSON.stringify(e)}`.trimEnd()),
    ].join("\n");
  }
  if (result.source === "additionalcontext" && result.raw) {
    const truncated = result.raw.length > 30_000;
    return [
      "Run logs (V1 run context — this environment/run predates or bypasses V2 logging):",
      truncated ? `${result.raw.slice(0, 30_000)}\n… (truncated)` : result.raw,
    ].join("\n");
  }
  return [
    "No action-level logs are available for this run.",
    "V2 action logs are documented for runs triggered from a cloud flow via the desktop-flows connector; API-triggered runs may record none. The run's outcome, error fields, and outputs are still available via get_desktop_flow_run.",
  ].join("\n");
}

const LICENSE_ERROR_PATTERN = /license|unattended.*add.?on|premium/i;

export async function handleDiagnoseDesktopFlowRun(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = sessionIdSchema.parse(input);
  logger.info({ sessionId: parsed.sessionId }, "Executing diagnose_desktop_flow_run");
  const { session: s, machine, logs } = await api.getRunDiagnostics(parsed.sessionId);
  const status = describeSessionStatus(s.statuscode);
  const terminal = s.statuscode !== undefined && TERMINAL_SESSION_STATUSES.has(s.statuscode);

  const lines: string[] = [
    `Run ${s.flowsessionid}: ${status}${terminal ? "" : " (still in progress)"}`,
    `Started: ${s.startedon ?? "not started"}${s.completedon ? ` — Completed: ${s.completedon}` : ""}`,
  ];

  if (s.statuscode === 4) {
    lines.push("", "The run succeeded — nothing to diagnose. Outputs: get_desktop_flow_run.");
    return lines.join("\n");
  }

  if (s.errorcode || s.errormessage) {
    lines.push(
      "",
      `Error: ${s.errorcode ?? "unknown"} — ${s.errormessage ?? "no message"}`,
    );
    if (s.errordetails) lines.push(`Details: ${s.errordetails}`);
  } else if (!terminal) {
    lines.push("", "No error recorded yet — the run is queued or executing. Poll get_desktop_flow_run.");
  }

  // Findings, most likely cause first.
  const findings: string[] = [];
  const errorText = `${s.errorcode ?? ""} ${s.errormessage ?? ""} ${s.errordetails ?? ""}`;

  if (LICENSE_ERROR_PATTERN.test(errorText)) {
    findings.push(
      "Licensing: the error text points at licensing. Attended runs need Power Automate Premium on the desktop-flow connection's owner; unattended runs additionally need a Process license allocated to the machine (legacy name: Unattended RPA add-on).",
    );
  }
  if (s.statuscode === 10) {
    findings.push(
      "Timeout: the run exceeded its timeout (default 3 hours unless run_desktop_flow set one). Long-running flows can pass timeoutSeconds up to 86400.",
    );
  }
  if (machine) {
    const machineStatus = describeMachineStatus(machine.statuscode);
    const heartbeat = formatHeartbeatAge(machine.lastheartbeatdate);
    const stale =
      machine.lastheartbeatdate !== undefined &&
      Date.now() - Date.parse(machine.lastheartbeatdate) > 10 * 60 * 1000;
    lines.push("", `Machine: ${machine.name ?? machine.flowmachineid} — ${machineStatus} — ${heartbeat}`);
    if (machine.statuscode !== undefined && machine.statuscode !== 1) {
      findings.push(
        `Machine health: the machine reports ${machineStatus}${machine.statuscode === 3 ? " — reconnect it via the Power Automate machine-runtime app" : ""}${machine.statuscode === 11 ? " — it needs the current group key" : ""}. Runs cannot land on an unhealthy machine.`,
      );
    } else if (stale) {
      findings.push(
        `Machine health: no heartbeat for over 10 minutes (${heartbeat}) — the machine may be off, asleep, or disconnected.`,
      );
    }
  } else if (s.gateway) {
    lines.push("", `Machine: ${s.gateway} (record not readable — it may not be shared with you)`);
  }

  if (findings.length > 0) {
    lines.push("", "Findings:", ...findings.map((f) => `  - ${f}`));
  } else if (terminal && s.statuscode !== 4) {
    lines.push(
      "",
      "No machine or licensing signal stands out — the failure is likely inside the flow's own actions.",
    );
  }

  if (logs.source !== "none") {
    const tailNote =
      logs.source === "flowlog"
        ? `${logs.entries?.length ?? 0} action-log entries available`
        : "V1 run context available";
    lines.push("", `Logs: ${tailNote} — get_desktop_flow_run_logs for the detail.`);
  } else if (terminal && s.statuscode !== 4) {
    lines.push(
      "",
      "Logs: none recorded for this run (API-triggered runs often skip the V2 log store). The error fields above are the primary evidence.",
    );
  }

  return lines.join("\n");
}

export async function handleListDesktopFlowConnections(
  api: DesktopFlowsApi,
  _input: unknown,
): Promise<string> {
  logger.info("Executing list_desktop_flow_connections");
  const refs = await api.listConnectionReferences();
  if (refs.length === 0) {
    return NO_CONNECTIONS_GUIDANCE;
  }
  return [
    `Desktop-flows connection references (${refs.length}):`,
    ...refs.map(
      (r) =>
        `  ${r.connectionreferencedisplayname ?? r.connectionreferencelogicalname} — logical name: ${r.connectionreferencelogicalname}${r.connectionid ? " (bound to a connection)" : " (NOT bound — bind it to a connection in the solution before use)"}`,
    ),
    "",
    'Use one with run_desktop_flow: pass the logical name as connectionName and set isConnectionReference: true. Only references bound to a real connection will run.',
  ].join("\n");
}

// =============================================================================
// Machines
// =============================================================================

function formatHeartbeatAge(lastheartbeatdate?: string): string {
  if (!lastheartbeatdate) return "never seen";
  const then = Date.parse(lastheartbeatdate);
  if (!Number.isFinite(then)) return lastheartbeatdate;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `last seen ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `last seen ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `last seen ${hours}h ago`;
  return `last seen ${Math.floor(hours / 24)}d ago`;
}

function machineStatusLine(machine: FlowMachine): string {
  const status = describeMachineStatus(machine.statuscode);
  const state = machine.statecode !== undefined ? MACHINE_STATE[machine.statecode] : undefined;
  // Maintenance/drain live on statecode; surface it when it disagrees with statuscode.
  return state && state !== "Active" && !status.includes(state) ? `${status} (${state})` : status;
}

function formatMachineLine(machine: FlowMachine): string {
  const hosting = MACHINE_HOSTING_TYPE[machine.hostingtype ?? 0] ?? "Physical";
  const capacity =
    machine.sessioncapacity !== undefined ? `, capacity ${machine.sessioncapacity}` : "";
  return `  ${machine.name ?? machine.flowmachineid} (${machine.flowmachineid}) — ${machineStatusLine(machine)} — ${hosting}${capacity} — ${formatHeartbeatAge(machine.lastheartbeatdate)}`;
}

export async function handleListMachines(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = listMachinesSchema.parse(input);
  logger.info(parsed, "Executing list_machines");
  const machines = await api.listMachines(parsed);
  if (machines.length === 0) {
    return [
      "No desktop-flow machines are registered (or visible to you) in this environment.",
      "To register one: install Power Automate for desktop (2.29+) on the Windows machine and sign in — it registers into the selected environment. Machines are user-owned; ask the owner to share theirs if you expect to see it.",
    ].join("\n");
  }
  return [
    `Desktop-flow machines (${machines.length}):`,
    ...machines.map(formatMachineLine),
    "",
    "Inspect one with get_machine.",
  ].join("\n");
}

export async function handleGetMachine(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = getMachineSchema.parse(input);
  logger.info({ machineId: parsed.machineId }, "Executing get_machine");
  const m = await api.getMachine(parsed.machineId);
  const hosting = MACHINE_HOSTING_TYPE[m.hostingtype ?? 0] ?? "Physical";
  const keyHealth =
    m.keydeliverystatus !== undefined ? KEY_DELIVERY_STATUS[m.keydeliverystatus] : undefined;
  const lines = [
    `Machine: ${m.name ?? m.flowmachineid} (${m.flowmachineid})`,
    `Status: ${machineStatusLine(m)}`,
    `Hosting: ${hosting}`,
    `Heartbeat: ${formatHeartbeatAge(m.lastheartbeatdate)}`,
    m.sessioncapacity !== undefined
      ? `Session capacity: ${m.sessioncapacity} (concurrent unattended runs need one Process license each)`
      : undefined,
    m.agentversion ? `Agent version: ${m.agentversion}` : undefined,
    m._flowmachinegroupid_value ? `Machine group: ${m._flowmachinegroupid_value}` : undefined,
    keyHealth && keyHealth !== "Default"
      ? `Group key health: ${keyHealth} — the machine may need re-keying via the machine-runtime app`
      : undefined,
    m.hostedmachineerror ? `Hosted machine error: ${m.hostedmachineerror}` : undefined,
    m.overcapacitysince ? `Over capacity since: ${m.overcapacitysince}` : undefined,
  ].filter((l): l is string => l !== undefined);
  return lines.join("\n");
}

export async function handleListMachineGroups(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = listMachineGroupsSchema.parse(input);
  logger.info(parsed, "Executing list_machine_groups");
  const groups = await api.listMachineGroups(parsed);
  if (groups.length === 0) {
    return [
      "No machine groups found (or visible to you) in this environment.",
      "Machine groups load-balance unattended desktop flow runs across registered machines. Create one at make.powerautomate.com > Monitor > Machines > Machine groups, then add machines through the machine-runtime app.",
    ].join("\n");
  }
  return [
    `Machine groups (${groups.length}):`,
    ...groups.map((g) => {
      const type = g.flowgrouptype !== undefined ? MACHINE_GROUP_TYPE[g.flowgrouptype] : undefined;
      const provisioning =
        g.provisioningstate && g.provisioningstate !== "Provisioned"
          ? ` — provisioning: ${g.provisioningstate}${g.provisioningerror ? ` (${g.provisioningerror})` : ""}`
          : "";
      return `  ${g.name ?? g.flowmachinegroupid} (${g.flowmachinegroupid}) — ${g.machineCount} machine${g.machineCount === 1 ? "" : "s"}${type ? ` — ${type}` : ""}${provisioning}`;
    }),
  ].join("\n");
}

export async function handleRestartHostedMachine(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = getMachineSchema.parse(input);
  logger.info({ machineId: parsed.machineId }, "Executing restart_hosted_machine");
  const result = await api.restartHostedMachine(parsed.machineId);
  return [
    `Restart requested for hosted machine ${result.name ?? parsed.machineId}.`,
    "Any in-flight run on it will fail and the machine will be briefly unavailable; check status with get_machine.",
  ].join("\n");
}
